import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="user--notifications"
import { get } from "@rails/request.js"
import { rethrowUnlessAbort } from "../../utils/fetch-error.util"
import { DETACH_GRACE_MS } from "../../utils/notification-bell.util"

// Whether the render in progress was caused by the user — a click, a form
// submission, Back/Forward — rather than by the page refreshing itself (a
// broadcast <turbo-stream action="refresh">, the export's timed fallback
// Turbo.visit). The user's intent is consumed by the next page render, so a
// click that never renders the page (a frame modal, a 422 form) costs at most
// one extra fetch instead of sticking.
let userIntent = false
let userCausedRender = false
for (const type of ["turbo:click", "turbo:submit-start"]) {
  document.addEventListener(type, () => { userIntent = true }, true)
}
window.addEventListener("popstate", () => { userIntent = true })
document.addEventListener("turbo:before-render", () => {
  userCausedRender = userIntent
  userIntent = false
})
document.addEventListener("turbo:render", () => { userCausedRender = false })

export default class extends Controller {
  connect() {
    // A permanent bell (shared/menu/_top_menu, on an export page that refreshes
    // itself every second) is detached and re-inserted by every render, and
    // Stimulus reuses this instance for the moved node. When the page refreshed
    // itself, the list it loaded — or the request still in flight — is kept.
    // A render the user caused refetches, so a notification just clicked shows
    // as read. State lives on the instance, never on the node: a node cloned
    // from Turbo's snapshot cache is a new instance and fetches.
    const reattached = this.detachTimer !== undefined
    clearTimeout(this.detachTimer)
    this.detachTimer = undefined
    if (reattached && !userCausedRender && (this.loaded || this.inFlight)) return

    this.fetch()
  }

  disconnect() {
    if (this.element.hasAttribute("data-turbo-permanent")) {
      this.detachTimer = setTimeout(() => this.release(), DETACH_GRACE_MS)
    } else {
      this.release()
    }
  }

  // Every Turbo visit replaces a plain top menu, so a request must not outlive
  // the menu that asked for it (LUC-893). The abort is swallowed; a real
  // failure of our own endpoint still re-raises (frontend/stimulus-controllers.md §1).
  fetch() {
    this.abortController?.abort()
    const controller = new AbortController()
    this.abortController = controller
    this.loaded = false
    this.inFlight = true
    get(`/notifications`, { responseKind: "turbo-stream", signal: controller.signal })
      .then((response) => {
        // request.js resolves a 4xx/5xx too and renders nothing then: only an
        // OK answer filled the list.
        if (this.abortController === controller) this.loaded = response.ok
      })
      .finally(() => {
        if (this.abortController === controller) this.inFlight = false
      })
      .catch(rethrowUnlessAbort)
  }

  release() {
    this.detachTimer = undefined
    this.abortController?.abort()
  }
}
